import React, {useEffect, useReducer, useState} from "react";
import DemandOffersAccessService from "services/DemandOffersAccessService";
import {useParams} from "react-router";
import {connect} from "react-redux";
import Loading from "components/Loading";
import Tr from "translations/Translations";
import GeneralInfoEntry from "../generalInfo/components/GeneralInfoEntry";
import {Box, makeStyles} from "@material-ui/core";
import Typography from "components/Typography";
import GridBlock from "components/GridBlock";
import Form from "components/Form";
import VS from "constants/ValidationShema";
import Checkbox from "components/Checkbox";
import {StageSections} from "../../../constants/StageSections";
import WorkableButton from "components/button/WorkableButton";
import useSnackbar from "hooks/useSnackbar";
import Select, {createSelectItem} from "components/Select";
import iconWorksReducer from "libs/iconWorksReducer";
import Button from "components/Button";
import Dialog from "components/Dialog";
import moment from "moment/moment";
import DateFormats from "constants/DateFormats";

const useStyle = makeStyles(theme => ({
    root: {
        width: '100%'
    },
    marginLeft: {
        marginLeft: theme.spacing(2),
    },
    accessUser: {
        display: "flex",
        alignItems: "center",
    },
    button: {
        marginLeft: theme.spacing(2),
    },
    accessAdminSelect: {
        width: "50%"
    }
}))

function AccessPanel(
    {
        accessUsers,
        sections,
        demandOffersAccessUsersHistory
    }) {

    const params = useParams();
    const demandId = params.id;
    const stageId = params.stageId;
    const classes = useStyle();
    const accessSection = sections[StageSections.ACCESS];
    const [editAdmin, setEditAdmin] = useState(false);
    const snackbar = useSnackbar();
    const [buttonWorks, dispatchButtonWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        saveAdmin: false,
        saveUsers: false,
    });
    const [history, setHistory] = useState(false);

    useEffect(() => {
        DemandOffersAccessService.getUsers(demandId, stageId).then();

        return () => {
            DemandOffersAccessService.clearResources();
        }
        // eslint-disable-next-line
    }, [])

    const validationSchemaAdmin = (au) => {
        const shape = {
            adminId: VS.any().test('au_admin', Tr('offer_access_change_admin_choose_different_admin'), (e) => {
                return e !== au.admin.identity;
            }),
            admin: VS.any()
        }
        return VS.object(shape);
    }

    const validationSchemaUsers = () => {
        return VS.object();
    }

    const onSubmitAdmin = (values) => {
        dispatchButtonWorks({type: 'saveAdmin', works: true})
        const payload = {adminId: values.adminId};
        DemandOffersAccessService.changeAdmin(demandId, stageId, payload).then(resp => {
            if (resp.success) {
                window.location.href = accessUsers.redirectAfterAdminChange;
            }
        });
    }

    const onSubmitUsers = (values) => {
        dispatchButtonWorks({type: 'saveUsers', works: true})
        const payload = {list: values.availableUsers.filter(x => x.selected).map(x => x.identity)};
        DemandOffersAccessService.giveAccess(demandId, stageId, payload).then(() => {
            DemandOffersAccessService.getUsers(demandId, stageId).then(() => {
                dispatchButtonWorks({type: 'saveUsers', works: false})
                snackbar.enqueueSuccessSnackbar(Tr('offer_access_users_granted'))
            });
        });
    }

    const initialValuesAdmin = () => {
        const o = {};
        o.admin = accessUsers.admin;
        o.adminId = accessUsers.admin.identity;
        return o;
    }

    const initialValuesUsers = () => {
        const o = {};
        o.availableUsers = accessUsers.availableUsers.map(user => {
            return {
                selected: accessUsers.users.some(x => x.identity === user.identity),
                ...user
            }
        });
        return o;
    }

    const adminUserList = () => {
        const arr = [];
        arr.push(createSelectItem(accessUsers.admin.identity, `${accessUsers.admin.firstName} ${accessUsers.admin.lastName} (${accessUsers.admin.email})`));
        accessUsers.availableUsers.forEach(user => {
            arr.push(createSelectItem(user.identity, `${user.firstName} ${user.lastName} (${user.email})`));
        });
        return arr;
    }

    const loadHistory = () => {
        setHistory(true);
        DemandOffersAccessService.getHistory(demandId, stageId).then(() => {});
    }

    const clearHistory = () => {
        setHistory(false);
        DemandOffersAccessService.clearHistory();
    }

    if (!accessUsers || !accessSection) return <Loading/>;

    return (<div className={classes.root}>
                <GridBlock column>
                    <GridBlock justify='space-between'>
                        <Typography></Typography>
                        <Button onClick={() => loadHistory()} variant={'outlined'}>{Tr('offer_access_history_button')}</Button>
                    </GridBlock>
                    <Form
                        initialValues={initialValuesAdmin()}
                        validationSchema={validationSchemaAdmin(accessUsers)}
                        onSubmit={onSubmitAdmin}
                        id={'offerAccessAdminForm'}
                    >
                        {(formikPropsAdmin) => (
                            <GeneralInfoEntry biggerLabel label={Tr('offer_access_admin')}>
                                {accessSection?.editable && !!accessUsers.supportUser ?
                                    <GridBlock justify='space-between'>
                                        <Typography></Typography>
                                        {!!editAdmin &&
                                            <GridBlock>
                                                <WorkableButton
                                                    className={classes.button}
                                                    working={buttonWorks.saveAdmin}
                                                    onClick={() => formikPropsAdmin.submitForm()}
                                                    disabled={buttonWorks.anyWorks}
                                                >{Tr('offer_access_change_admin_confirm')}</WorkableButton>
                                                <WorkableButton
                                                    className={classes.button}
                                                    onClick={() => {
                                                        formikPropsAdmin.setFieldValue('admin', accessUsers.admin);
                                                        formikPropsAdmin.setFieldValue('adminId', accessUsers.admin.identity);
                                                        setEditAdmin(false);
                                                    }}
                                                    disabled={buttonWorks.anyWorks}
                                                >{Tr('cancel')}</WorkableButton>
                                            </GridBlock>
                                        }
                                        {!editAdmin &&
                                            <WorkableButton working={buttonWorks.saveAdmin} onClick={() => setEditAdmin(true)}>{Tr('offer_access_change_admin')}</WorkableButton>
                                        }
                                    </GridBlock> : <br/>
                                }
                                {accessSection?.editable && !!accessUsers.supportUser && !!editAdmin ?
                                    <Select
                                        className={classes.accessAdminSelect}
                                        formikProps={formikPropsAdmin}
                                        id={'adminId'}
                                        required
                                        items={adminUserList()}
                                    />
                                    :
                                    <Typography>{accessUsers.admin.firstName} {accessUsers.admin.lastName} ({accessUsers.admin.email})</Typography>
                                }
                            </GeneralInfoEntry>
                        )}
                    </Form>
                    <Form
                        initialValues={initialValuesUsers()}
                        validationSchema={validationSchemaUsers()}
                        onSubmit={onSubmitUsers}
                        id={'offerAccessUsersForm'}
                    >
                        {(formikProps) => (
                        <GeneralInfoEntry biggerLabel label={Tr('offer_access_users')}>
                            {accessSection?.editable ?
                            <GridBlock justify='space-between'>
                                <Typography></Typography>
                                <WorkableButton
                                    working={buttonWorks.saveUsers}
                                    onClick={() => formikProps.submitForm()}
                                    disabled={buttonWorks.anyWorks}
                                >{Tr('save_changes')}</WorkableButton>
                            </GridBlock> : <br/>
                            }
                            {(!accessUsers.availableUsers || accessUsers.availableUsers.length === 0) && <Typography>{Tr('offer_access_users_no_available')}</Typography>}
                            {!!accessUsers.availableUsers && accessUsers.availableUsers.length > 0 && accessUsers.availableUsers.map((user, index) => {
                                return (
                                    <GridBlock itemClassName={classes.accessUser} key={index}>
                                        <Checkbox
                                            id={`availableUsers.${index}.selected`}
                                            formikProps={formikProps}
                                            checkboxClassName={classes.checkbox}
                                            htmlId={`availableUsersId${user.identity}`}
                                            disabled={!accessSection.editable}
                                            label={`${user.firstName} ${user.lastName} (${user.email})`}
                                        />
                                    </GridBlock>
                                )
                            })}
                        </GeneralInfoEntry>
                        )}
                    </Form>
                </GridBlock>
                {!!history &&
                    <Dialog
                        open
                        title={Tr('offer_access_history_popup')}
                        maxWidth="md"
                        onClose={() => clearHistory()}
                    >
                        {!demandOffersAccessUsersHistory && <Loading />}
                        {!!demandOffersAccessUsersHistory &&
                            <GridBlock
                                column
                                alignItems='center'
                            >
                                {demandOffersAccessUsersHistory.map((h, index) => {
                                    const secondPart = h.action !== 'join' && h.action !== 'leave';
                                    return (
                                        <GridBlock
                                            key={`history${index}`}
                                            wrap='nowrap'
                                            justify='space-between'
                                        >
                                            <Typography variant='body1'>
                                                <Box component='span' fontWeight='fontWeightBold'>{`${h.changingUserName}`}</Box>
                                                {` `}
                                                {Tr(`offer_access_history_action_${h.action}`)}
                                                {secondPart && ` `}
                                                {secondPart && Tr('offer_access_history_popup_to_user')}
                                                {secondPart && ` `}
                                                {secondPart && <Box component='span' fontWeight='fontWeightBold'>{`${h.user.firstName} ${h.user.lastName}`}</Box>}
                                            </Typography>
                                            <Typography
                                                variant='body1'
                                            >
                                                {moment(h.dateInserted).format(DateFormats.DATETIME_FORMAT)}
                                            </Typography>
                                        </GridBlock>
                                    )})
                                }
                            </GridBlock>
                        }
                    </Dialog>
                }
            </div>
    );
}

export default connect((state) => ({
    accessUsers: state.resources.demandOffersAccessUsers,
    sections: state.resources.demandStageSectionsList,
    demandOffersAccessUsersHistory: state.resources.demandOffersAccessUsersHistory,
}), {})(AccessPanel);